import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import Fuse from 'fuse.js';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Combobox } from "../components/ui/combobox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import LocationSelector from "../components/LocationSelector";
import { Users, Search, Plus, X, Trash2 } from 'lucide-react';
import EmployeesPanel from '@/components/EmployeesPanel';
import IncidentOriginChip from '@/components/IncidentOriginChip';
import { CommanServices } from '@/services/commanServices';

// Global Department Service - Single Source of Truth with Performance Optimization
const DepartmentService = {
  departments: [],
  listeners: [],
  lastFetch: 0,
  fetchTimeout: null,
  CACHE_DURATION: 30000, // 30 seconds cache
  DEBOUNCE_DELAY: 500, // 500ms debounce

  // Add listener for real-time updates
  addListener(callback) {
    this.listeners.push(callback);
  },

  // Remove listener
  removeListener(callback) {
    this.listeners = this.listeners.filter(l => l !== callback);
  },

  // Notify all listeners of department changes
  notify() {
    this.listeners.forEach(callback => callback(this.departments));
  },

  // Debounced fetch departments to prevent excessive api calls
  async fetchDepartments() {
    return new Promise((resolve) => {
      // Clear existing timeout
      if (this.fetchTimeout) {
        clearTimeout(this.fetchTimeout);
      }

      // Set new debounced timeout
      this.fetchTimeout = setTimeout(async () => {
        const result = await this._actualFetchDepartments();
        resolve(result);
      }, this.DEBOUNCE_DELAY);
    });
  },

  // Actual fetch implementation with caching
  async _actualFetchDepartments() {
    try {
      const now = Date.now();

      // Return cached data if still valid
      if (this.departments.length > 0 && (now - this.lastFetch) < this.CACHE_DURATION) {
        console.log('🚀 DEPARTMENT SERVICE: Using cached departments (performance optimization)');
        this.notify();
        return this.departments;
      }

      console.log('🔗 DEPARTMENT SERVICE: Fetching departments with location counts (optimized single query)');

      // PERFORMANCE FIX: Use single optimized endpoint instead of N+1 queries
      const response = await CommanServices.departmentslocationcounts();
      if (response.status) {
        // Filter out null/invalid departments
        const validDepartments = (response.data || []).filter(dept =>
          dept &&
          dept.id &&
          dept.department_name &&
          typeof dept.department_name === 'string'
        );

        // CRITICAL FIX: VERSIONED MERGE for departments (mirrors Primary Toggle zones pattern)
        if (this.departments.length === 0) {
          // Initial load - use all fresh data
          this.departments = validDepartments;
        } else {
          // Create maps for efficient lookup
          const prevDepartmentsMap = new Map(this.departments.map(d => [d.id, d]));
          const mergedDepartments = [];

          validDepartments.forEach(freshDepartment => {
            const prevDepartment = prevDepartmentsMap.get(freshDepartment.id);

            if (!prevDepartment) {
              // New department - use fresh data
              mergedDepartments.push(freshDepartment);
              return;
            }

            // VERSIONED MERGE: Only update leader_count if server version is newer AND has valid counts
            const serverVersion = freshDepartment.membersVersion || 0;
            const localVersion = prevDepartment.membersVersion || 0;

            // Check if fresh data has DEFINED leader_count field (mirrors Primary Toggle count check)
            const hasValidLeaderCount = typeof freshDepartment.leader_count === 'number';

            if (hasValidLeaderCount && serverVersion >= localVersion) {
              // Server data is newer and has valid leader count - use server data
              console.debug(`[departments:merge] id=${freshDepartment.id?.substring(0, 8)}... local.v=${localVersion} server.v=${serverVersion} action=applied leader_count=${freshDepartment.leader_count}`);
              mergedDepartments.push(freshDepartment);
            } else if (!hasValidLeaderCount && typeof prevDepartment.leader_count === 'number') {
              // Server data is missing leader count but local has valid count - preserve local
              console.debug(`[departments:merge] id=${freshDepartment.id?.substring(0, 8)}... local.v=${localVersion} server.v=${serverVersion} action=skipped(missing_leader_count)`);
              mergedDepartments.push({
                ...freshDepartment,
                leader_count: prevDepartment.leader_count,
                membersVersion: prevDepartment.membersVersion
              });
            } else {
              // Use server data as fallback
              console.debug(`[departments:merge] id=${freshDepartment.id?.substring(0, 8)}... local.v=${localVersion} server.v=${serverVersion} action=fallback`);
              mergedDepartments.push(freshDepartment);
            }
          });

          this.departments = mergedDepartments;
        }
        this.lastFetch = now;

        console.log('✅ DEPARTMENT SERVICE: Fetched', validDepartments.length, 'departments with location counts in single query');

        this.notify();
        return this.departments;
      }

    } catch (error) {
      console.error("❌ DEPARTMENT SERVICE: Error fetching departments:", error);

      // Fallback to basic departments endpoint if optimized endpoint fails
      try {
        console.log('🔄 DEPARTMENT SERVICE: Falling back to basic departments endpoint');
        const fallbackResponse = await CommanServices.departments();
        if (fallbackResponse.status) {
          const basicDepartments = (fallbackResponse.data || []).filter(dept =>
            dept && dept.id && dept.department_name
          );

          this.departments = basicDepartments.map(dept => ({
            ...dept,
            // authoritative_location_count: (dept.associated_locations || []).length
          }));

          this.notify();
          return this.departments;
        }

      } catch (fallbackError) {
        console.error("❌ DEPARTMENT SERVICE: Fallback also failed:", fallbackError);
        this.departments = [];
        this.notify();
        return [];
      }
    }
  },

  // Force refresh (bypasses cache)
  async forceRefresh() {
    this.lastFetch = 0;
    return this._actualFetchDepartments();
  },

  // Get current departments
  getDepartments() {
    return this.departments;
  }
};

// Make service globally available
window.DepartmentService = DepartmentService;

const DepartmentsAndUnits = () => {
  const [departments, setDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  // ENHANCED SEARCH: Replace basic searchTerm with powerful search functionality
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [rightPanelWidth, setRightPanelWidth] = useState(() => Math.floor(window.innerWidth * 0.35)); // Default ~35% of screen width
  const [isResizing, setIsResizing] = useState(false);

  // App Users Config integration
  const [userConfigs, setUserConfigs] = useState([]);
  const [eligibleMembers, setEligibleMembers] = useState([]);

  // Resizer functionality
  const handleMouseDown = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isResizing) return;

    const containerWidth = e.currentTarget.offsetWidth || window.innerWidth - 264; // Account for sidebar width
    const newWidth = containerWidth - e.clientX + 264; // Calculate from right edge

    // Set min and max width constraints
    const minWidth = 300;
    const maxWidth = Math.min(800, containerWidth * 0.6);

    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setRightPanelWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  // Add global mouse event listeners for resizing
  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Employee management state
  const [showEmployeesPanel, setShowEmployeesPanel] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(true); // State to control banner visibility
  const [formData, setFormData] = useState({
    department_name: "",
    associated_locations: [],
    department_leader: "",
    members: []
  });

  const [suggestions, setSuggestions] = useState({
    locations: [],
    members: [],
    allUsers: [] // Add all users for member search
  });

  useEffect(() => {
    fetchDepartments();
    fetchSuggestions();
    fetchUserConfigs();
    fetchAllUsers(); // Fetch all users for member selection

    // PERFORMANCE FIX: Consolidated debounced refresh handler
    let refreshTimeout = null;
    const debouncedRefresh = () => {
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
      refreshTimeout = setTimeout(() => {
        console.log('🚀 Departments & Units: Debounced refresh triggered');
        DepartmentService.forceRefresh(); // Use force refresh to bypass cache when needed
        fetchUserConfigs();
      }, 1000); // 1 second debounce for data refresh
    };

    // Subscribe to global department updates
    const handleDepartmentUpdate = (updatedDepartments) => {
      setDepartments(updatedDepartments);
    };
    DepartmentService.addListener(handleDepartmentUpdate);

    // PERFORMANCE FIX: Use debounced refresh for all real-time events
    const handleDepartmentChanged = (event) => {
      console.log('🔄 Departments & Units: Department updated event received (debounced):', event.detail);
      debouncedRefresh();
    };

    // Listen for location-department association changes from Locations & Zones
    const handleLocationDepartmentChanged = (event) => {
      console.log('🔄 Departments & Units: Location department changed event received (debounced):', event.detail);
      const { location_id, location_name, department_id, department_name, action } = event.detail;

      // Use debounced refresh instead of immediate fetch
      debouncedRefresh();

      // If we're currently editing the affected department, refresh its form data immediately
      if (selectedDepartment && selectedDepartment.id === department_id) {
        console.log('🔄 Departments & Units: Updating form for currently selected department:', selectedDepartment.department_name);

        // Update form data with slight delay to allow backend to settle
        setTimeout(async () => {
          try {
            const response = await CommanServices.getbyIddepartments(department_id);
            if (response.status) {
              const updatedDepartment = response.data;

              console.log('🔄 Departments & Units: Fresh department data from api:', updatedDepartment);

              setSelectedDepartment(updatedDepartment);
              setFormData({
                department_name: updatedDepartment.department_name,
                department_role: updatedDepartment.department_role,
                description: updatedDepartment.description || "",
                leaders: updatedDepartment.leaders || [],
                associated_locations: updatedDepartment.associated_locations || [],
                is_active: updatedDepartment.is_active
              });

              console.log('🔥 Departments & Units: Form data updated successfully');
              console.log('🔥 Departments & Units: New form associated_locations:', updatedDepartment.associated_locations);
            }


          } catch (error) {
            console.error('❌ Error refreshing selected department data:', error);
          }
        }, 500); // Same delay as App Users Config pattern
      }
    };

    // Listen for department location changes from Locations & Zones
    const handleDepartmentLocationChanged = (event) => {
      console.log('🔥 Department location changed from Locations & Zones:', event.detail);

      // Refresh department list to update location counts
      fetchDepartments();

      // If we're currently editing the affected department, refresh its form data
      if (selectedDepartment && event.detail.department_id === selectedDepartment.id) {
        console.log('🔥 Refreshing affected department form data');
        editDepartment(selectedDepartment);
      }
    };

    const handleUserDepartmentChanged = (event) => {
      console.log('User department changed event:', event.detail);
      // Refresh user configs and departments for accurate member counts
      fetchUserConfigs();
      fetchDepartments();
    };

    // Listen for location deletions to refresh department data and update department cards
    const handleLocationDeleted = (event) => {
      console.log('🔥 Location deleted, refreshing departments to update location counts:', event.detail);
      fetchDepartments(); // Refresh departments to update location counts in department cards

      // If we were editing a department, refresh its form data to show updated locations
      if (selectedDepartment) {
        editDepartment(selectedDepartment);
      }
    };

    // LEADER TOGGLE: Listen for department member changes (mirrors Primary Toggle pattern)
    const handleDepartmentMembersChanged = (event) => {
      console.log('🔄 Departments & Units: Department members changed event received:', event.detail);
      const { department_id, leader_count, version } = event.detail;

      // VERSIONED MERGE - only update if we have newer data (mirrors Primary Toggle)
      setDepartments(prevDepartments =>
        prevDepartments.map(dept => {
          if (dept.id === department_id) {
            const localVersion = dept.membersVersion || 0;
            if (version >= localVersion) {
              console.log(`[departments:event] Updating ${dept.department_name} leader count: ${leader_count} (v:${version})`);
              return {
                ...dept,
                leader_count: leader_count,
                membersVersion: version
              };
            } else {
              console.log(`[departments:event] Skipping ${dept.department_name} - local version newer`);
            }
          }
          return dept;
        })
      );

      // Do not call editDepartment() here: it runs setShowEmployeesPanel(false) and would
      // close the Members panel after every leader/member PATCH. EmployeesPanel already
      // refreshes selectedDepartment + departments via setSelectedDepartment / setDepartments.
    };
    // ACCESS LEVEL SYNC: Handled directly in EmployeesPanel component

    window.addEventListener('department.updated', handleDepartmentChanged);
    window.addEventListener('user.departments.changed', handleUserDepartmentChanged);
    window.addEventListener('department.locations.changed', handleLocationDepartmentChanged);
    window.addEventListener('location.departments.changed', handleDepartmentLocationChanged);
    window.addEventListener('location.deleted', handleLocationDeleted);
    window.addEventListener('department.members.changed', handleDepartmentMembersChanged); // NEW: Leader toggle support

    return () => {
      DepartmentService.removeListener(handleDepartmentUpdate);
      window.removeEventListener('department.updated', handleDepartmentChanged);
      window.removeEventListener('user.departments.changed', handleUserDepartmentChanged);
      window.removeEventListener('department.locations.changed', handleLocationDepartmentChanged);
      window.removeEventListener('location.departments.changed', handleDepartmentLocationChanged);
      window.removeEventListener('location.deleted', handleLocationDeleted);
      window.removeEventListener('department.members.changed', handleDepartmentMembersChanged); // NEW: Leader toggle support
    };
  }, [selectedDepartment]); // Add selectedDepartment to dependencies

  // Fetch all users for member selection (not restricted to department assignment)
  const fetchAllUsers = async () => {
    try {
      const response = await CommanServices.getuserconfigs();
      if (response.status) {
        setSuggestions(prev => ({
          ...prev,
          allUsers: response.data || []
        }));
      }
    } catch (error) {
      console.error("Error fetching all users:", error);
    }
  };

  // Refresh data when the module becomes active (user returns from App Users Config)
  useEffect(() => {
    fetchUserConfigs();
  }, []);

  // ENHANCED SEARCH: Comprehensive fuzzy search implementation
  const fuseOptions = {
    keys: [
      // Department/Unit Name
      { name: 'department_name', weight: 0.3 },
      // Description
      { name: 'description', weight: 0.2 },
      // Emails
      { name: 'members.contact', weight: 0.1 },
      { name: 'leaders.contact', weight: 0.1 },
      // Phone Numbers  
      { name: 'members.phone', weight: 0.1 },
      { name: 'leaders.phone', weight: 0.1 },
      // Employee names + emails
      { name: 'members.name', weight: 0.15 },
      { name: 'members.user_account', weight: 0.1 },
      // Leader names + emails
      { name: 'leaders.name', weight: 0.15 },
      { name: 'leaders.user_account', weight: 0.1 },
      // Associated locations (auto-generated labels)
      { name: 'associatedLocationLabels', weight: 0.2 }
    ],
    threshold: 0.4, // Fuzzy/typo tolerance (0.0 = exact, 1.0 = match anything)
    includeScore: true,
    includeMatches: true,
    ignoreLocation: true,
    useExtendedSearch: true // Enable field-aware search like "leader:ahmed"
  };

  const [fuse, setFuse] = useState(null);
  const [searchResults, setSearchResults] = useState([]);

  // Initialize Fuse.js when departments change
  useEffect(() => {
    if (departments.length > 0) {
      // Prepare departments with enhanced searchable data
      const searchableDepartments = departments.map(dept => {
        // Get associated location labels for search
        const locationLabels = dept.associated_locations ?
          dept.associated_locations.map(locId => {
            // Find location by ID and create auto-generated label
            // This would normally come from a locations lookup, for now use placeholder
            return `Location-${locId}`;
          }) : [];

        return {
          ...dept,
          associatedLocationLabels: locationLabels.join(' '),
          // Flatten members and leaders for easier search
          members: dept.members || [],
          leaders: dept.leaders || []
        };
      });

      const newFuse = new Fuse(searchableDepartments, fuseOptions);
      setFuse(newFuse);
    }
  }, [departments]);

  // Enhanced search with debouncing and field-aware queries
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      if (!searchQuery.trim()) {
        setFilteredDepartments(departments);
        setSearchResults([]);
        return;
      }

      if (!fuse) {
        // Fallback to basic search if Fuse not ready
        const basicFiltered = departments.filter(dept =>
          dept.department_name.toLowerCase().includes(searchQuery.toLowerCase())
        );
        setFilteredDepartments(basicFiltered);
        return;
      }

      // Parse field-aware queries
      let searchTerm = searchQuery;
      let fieldFilters = {};

      // Extract field-aware syntax: leader:ahmed, employee:fatima, location:ICU, phone:5458
      const fieldMatches = searchQuery.match(/(\w+):(\w+)/g);
      if (fieldMatches) {
        fieldMatches.forEach(match => {
          const [field, value] = match.split(':');
          fieldFilters[field] = value;
          searchTerm = searchTerm.replace(match, '').trim();
        });
      }

      // Perform fuzzy search
      let results;
      if (Object.keys(fieldFilters).length > 0) {
        // Field-aware search
        results = departments.filter(dept => {
          let matches = true;

          Object.entries(fieldFilters).forEach(([field, value]) => {
            switch (field.toLowerCase()) {
              case 'leader':
                const leaderMatch = dept.leaders?.some(leader =>
                  leader.name?.toLowerCase().includes(value.toLowerCase()) ||
                  leader.contact?.toLowerCase().includes(value.toLowerCase()) ||
                  leader.user_account?.toLowerCase().includes(value.toLowerCase())
                );
                if (!leaderMatch) matches = false;
                break;

              case 'employee':
                const employeeMatch = dept.members?.some(member =>
                  member.name?.toLowerCase().includes(value.toLowerCase()) ||
                  member.contact?.toLowerCase().includes(value.toLowerCase()) ||
                  member.user_account?.toLowerCase().includes(value.toLowerCase())
                );
                if (!employeeMatch) matches = false;
                break;

              case 'location':
                const locationMatch = dept.associatedLocationLabels?.toLowerCase().includes(value.toLowerCase());
                if (!locationMatch) matches = false;
                break;

              case 'phone':
                const phoneMatch = dept.members?.some(member =>
                  member.contact?.includes(value)
                ) || dept.leaders?.some(leader =>
                  leader.contact?.includes(value)
                );
                if (!phoneMatch) matches = false;
                break;
            }
          });

          // Also check if remaining search term matches
          if (searchTerm && matches) {
            const fuseResults = fuse.search(searchTerm);
            matches = fuseResults.some(result => result.item.id === dept.id);
          }

          return matches;
        });
      } else {
        // Regular fuzzy search
        const fuseResults = fuse.search(searchTerm);
        results = fuseResults.map(result => result.item);
      }

      setFilteredDepartments(results);
      setSearchResults(results);

    }, 250); // 250ms debounce - responsive but not excessive

  }, [searchQuery, fuse, departments]);

  // Clear search function
  const clearSearch = () => {
    setSearchQuery("");
    setFilteredDepartments(departments);
    setSearchResults([]);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Keyboard shortcuts: Escape to clear, Ctrl/Cmd+K to focus search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && searchQuery) {
        clearSearch();
        e.preventDefault();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        document.getElementById('departments-search')?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchQuery]);

  // Update eligible members when department is selected or userConfigs change
  useEffect(() => {
    if (formData.department_name && userConfigs.length > 0) {
      updateEligibleMembers();
    }
  }, [userConfigs, formData.department_name]);

  // Debug logging useEffect (remove this in production)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('DEBUG - Department name:', formData.department_name);
      console.log('DEBUG - User configs count:', userConfigs.length);
      console.log('DEBUG - Eligible members count:', eligibleMembers.length);
    }
  }, [formData.department_name, userConfigs, eligibleMembers]);

  // Fetch all user configurations from App Users Config
  const fetchUserConfigs = async () => {
    try {
      const response = await CommanServices.getuserconfigs();
      if (response.status) {
        setUserConfigs(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching user configs:", error);
      setUserConfigs([]);
    }
  };

  // Update eligible members based on department assignment in App Users Config
  const updateEligibleMembers = () => {
    if (!formData.department_name) {
      setEligibleMembers([]);
      return;
    }

    // Get the current department ID if editing existing department
    let departmentId = null;
    if (selectedDepartment && selectedDepartment.id) {
      departmentId = selectedDepartment.id;
    } else {
      // For new departments, try to find by name in the departments list
      const existingDept = departments.find(dept =>
        dept.department_name?.toLowerCase().trim() === formData.department_name.toLowerCase().trim()
      );
      if (existingDept) {
        departmentId = existingDept.id;
      }
    }

    const deptName = formData.department_name.toLowerCase().trim();

    // Filter users who have this department assigned in their App Users Config
    const eligible = userConfigs.filter(user => {
      if (!user.departments_units) {
        return false;
      }

      // Check if this department is in the user's assigned departments
      const assignedDepts = Array.isArray(user.departments_units) ? user.departments_units : [];
      const hasMatch = assignedDepts.some(dept => {
        // Try matching by ID first (preferred), then by name as fallback
        const matchById = departmentId && dept === departmentId;
        const matchByName = dept?.toLowerCase().trim() === deptName;
        return matchById || matchByName;
      });

      return hasMatch;
    });

    // Remove duplicates based on user_account
    const uniqueEligible = eligible.filter((user, index, self) =>
      index === self.findIndex(u => u.user_account === user.user_account)
    );

    setEligibleMembers(uniqueEligible);

    // Consistency rule: Remove members who are no longer assigned to this department
    if (formData.members && formData.members.length > 0) {
      const eligibleUserAccounts = uniqueEligible.map(user => user.user_account?.toLowerCase());
      const validMembers = formData.members.filter(member => {
        const isStillEligible = eligibleUserAccounts.includes(member.name?.toLowerCase());
        if (!isStillEligible && member.name) {
          console.log(`Removing member ${member.name} - no longer assigned to this department in App Users Config`);
        }
        return isStillEligible;
      });

      // Update members list if any were removed
      if (validMembers.length !== formData.members.length) {
        setFormData(prev => ({
          ...prev,
          members: validMembers
        }));
      }
    }
  };

  // Phone and Email validation functions
  const validatePhoneNumber = (phone) => {
    // E.164 format validation
    const e164Regex = /^\+[1-9]\d{1,14}$/;
    return e164Regex.test(phone);
  };

  const validateEmail = (email) => {
    // RFC 5322 compliant email validation
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(email);
  };

  // Helper to deduplicate arrays
  const deduplicateArray = (arr) => [...new Set(arr.filter(item => item && item.trim()))];

  // Get eligible members - now includes ALL users for direct selection
  const getEligibleMembers = () => {
    // Return all users, not just those assigned to this department
    // This allows adding any user directly from Departments & Units
    return suggestions.allUsers || [];
  };

  // Get user data from App Users Config by user account
  const getUserFromAppUsersConfig = (userAccount) => {
    return userConfigs.find(user =>
      user.user_account?.toLowerCase() === userAccount?.toLowerCase()
    );
  };

  // Auto-fill member data from App Users Config when member is selected
  const autoFillMemberFromAppUsersConfig = async (memberIndex, selectedUserAccount) => {
    const userData = getUserFromAppUsersConfig(selectedUserAccount);
    if (userData) {
      const updatedMember = {
        ...formData.members[memberIndex],
        name: selectedUserAccount,
        phone: userData.phone_number || '',
        email: userData.email || '',
        // Inherit settings from App Users Config
        reporting_enabled: userData.enable_duty_check || false,
        notification_enabled: userData.enable_notifications || false
      };

      const newMembers = [...formData.members];
      newMembers[memberIndex] = updatedMember;
      setFormData({ ...formData, members: newMembers });

      // AUTO-ASSIGN: Add this department to the user's profile in App Users Config
      await autoAssignDepartmentToUser(userData, formData.department_name);
    }
  };

  // Auto-assign department to user in App Users Config when added as member
  const autoAssignDepartmentToUser = async (userData, departmentName) => {
    try {
      // Get department ID
      const department = departments.find(d => d.department_name === departmentName);
      if (!department) return;

      // Update user's department assignments
      const currentDepartments = userData.departments_units || [];
      const departmentAlreadyAssigned = currentDepartments.includes(department.id);

      if (!departmentAlreadyAssigned) {
        const updatedUserData = {
          ...userData,
          departments_units: [...currentDepartments, department.id]
        };

        await CommanServices.updateuserconfigs(userData.id, updatedUserData);

        console.log(`Auto-assigned department "${departmentName}" to user "${userData.user_account}"`);

        // Refresh user configs to get latest data
        fetchUserConfigs();
        fetchAllUsers();
      }
    } catch (error) {
      console.error('Error auto-assigning department to user:', error);
    }
  };

  // Sync member changes back to App Users Config (bidirectional sync)
  const syncMemberToAppUsersConfig = async (memberData) => {
    try {
      const userData = getUserFromAppUsersConfig(memberData.name);
      if (userData) {
        const updatedUserData = {
          ...userData,
          phone_number: memberData.phone || userData.phone_number,
          email: memberData.email || userData.email,
          // Sync toggles bidirectionally
          enable_duty_check: memberData.reporting_enabled !== undefined ? memberData.reporting_enabled : userData.enable_duty_check,
          enable_notifications: memberData.notification_enabled !== undefined ? memberData.notification_enabled : userData.enable_notifications
        };

        await CommanServices.updateuserconfigs(userData.id, updatedUserData);

        console.log('Successfully synced member data to App Users Config:', memberData);

        // Refresh user configs to get latest data
        fetchUserConfigs();
      }
    } catch (error) {
      console.error('Error syncing to App Users Config:', error);
    }
  };

  const fetchDepartments = async () => {
    // Use global department service
    await DepartmentService.fetchDepartments();
    // Local state will be updated via the listener we set up in useEffect
  };

  const fetchSuggestions = async () => {
    try {
      const [members] = await Promise.all([
        CommanServices.getdepartmentssuggestionsmembers()
      ]);
      if (members.status) {
        setSuggestions({
          members: members.data,
          // Remove hardcoded locations - now using LocationSelector component
          incidentRulesData: []
        });
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    }
  };

  const handleSave = async () => {
    try {
      console.log("🔥 DEPARTMENT SAVE: Starting save process");
      console.log("🔥 DEPARTMENT SAVE: Current formData:", formData);
      console.log("🔗 DEPARTMENT SAVE: formData.associated_locations:", formData.associated_locations);

      // CRITICAL FIX: Include associated_locations in the save payload for both CREATE and UPDATE
      const saveData = {
        department_name: formData.department_name,
        associated_locations: formData.associated_locations || [] // FIX: Include locations in create payload
        // NOTE: members, leaders, and department_leader are omitted here because they are
        // managed exclusively by EmployeesPanel.jsx. Passing them here would overwrite
        // the members array due to legacy userConfigs clearing them out on load.
      };

      console.log("🔥 DEPARTMENT SAVE: Final saveData being sent to api:", saveData);

      let response;
      if (isEditing && selectedDepartment) {
        console.log("🔥 DEPARTMENT SAVE: Updating existing department:", selectedDepartment.id);
        response = await CommanServices.updatedepartments(selectedDepartment.id, saveData);

        // For updates, use the existing JOIN table logic for location sync
        if (formData.associated_locations && formData.associated_locations.length > 0) {
          console.log("🔗 DEPARTMENT SAVE: Handling location associations via JOIN table for update");
          await handleLocationChange(formData.associated_locations, true); // Skip form refresh to prevent reset
        }
      } else {
        console.log("🔥 DEPARTMENT SAVE: Creating new department WITH associated_locations");

        // SIMPLIFIED FIX: Send associated_locations directly in create payload
        // The backend DepartmentCreate model accepts associated_locations field
        response = await CommanServices.adddepartments(saveData);

        console.log("🔥 DEPARTMENT SAVE: New department created successfully WITH locations:", response.data);

        // Set the selected department immediately for editing mode
        if (response.status) {
          const newDepartment = response.data;
          setSelectedDepartment(newDepartment);
          setIsEditing(true);
        } else {
          alert(response.message);
        }

        // No need for complex location handling - backend handles it during create!
      }

      console.log("🔥 DEPARTMENT SAVE: API Response successful:", response.data);

      // Single success toast - no duplicates
      alert("Department saved successfully!");

      // Force refresh departments list to ensure UI is updated immediately
      await DepartmentService.forceRefresh();

      // Update selected department state
      if (!isEditing) {
        setSelectedDepartment(response.data);
        setIsEditing(true);
      } else {
        setSelectedDepartment(response.data);
      }

      // Update local form data with saved data to ensure UI reflects saved state
      setFormData(prev => ({
        ...prev,
        department_name: response.data.department_name,
        department_leader: response.data.department_leader,
        members: response.data.members,
        associated_locations: response.data.associated_locations || prev.associated_locations
      }));

      // Emit events for cross-module updates and bidirectional sync
      window.dispatchEvent(new CustomEvent('departmentUpdated', {
        detail: response.data
      }));
      window.dispatchEvent(new CustomEvent('department.updated', {
        detail: { department_id: response.data.id }
      }));

    } catch (error) {
      console.error("Error saving department:", error);
      console.error("Error response:", error.response);

      // Show proper error message to user
      let errorMessage = "Unknown error occurred";

      if (error.response) {
        // Server responded with error
        if (error.response.data) {
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else {
            errorMessage = JSON.stringify(error.response.data);
          }
        } else {
          errorMessage = `HTTP ${error.response.status}: ${error.response.statusText}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(`Error saving department: ${errorMessage}`);
    }
  };

  const handleDelete = async (id) => {
    try {
      // Get department details before deletion for cleanup
      const deptToDelete = departments.find(dept => dept.id === id);
      const departmentName = deptToDelete ? deptToDelete.department_name : 'Unknown';

      console.log('🔥 Starting department deletion:', departmentName, id);

      // Delete the department from backend
      const response = await CommanServices.deletebyIddepartments();


      // CRITICAL: Clean up all locations that have this department in their departments array
      try {
        console.log('🔥 Cleaning up locations associated with deleted department');

        // Get all locations to check for this department
        const locationsResponse = await CommanServices.locationszoneswithlinks();

        if (locationsResponse.status) {
          const allLocations = locationsResponse.data || [];

          for (const location of allLocations) {
            const locationDepartments = location.departments || [];

            // Check if this location has the deleted department
            const hasDepartment = locationDepartments.some(dept =>
              dept.department_id === id || dept.department_name === departmentName
            );

            if (hasDepartment) {
              console.log('🔥 Removing department from location:', location.zone_name);

              // Remove the deleted department from this location's departments array
              const updatedDepartments = locationDepartments.filter(dept =>
                dept.department_id !== id && dept.department_name !== departmentName
              );

              const updatedLocation = {
                ...location,
                departments: updatedDepartments
              };

              // Update the location in backend
              await CommanServices.updatelocationszones(location.id, updatedLocation);

              console.log('✅ Successfully cleaned up location:', location.zone_name);
            }
          }

          console.log('✅ Department cleanup completed for all locations');
        }

      } catch (cleanupError) {
        console.error('❌ Error during location cleanup:', cleanupError);
        // Don't fail the entire deletion if cleanup fails
      }

      // Refresh departments list immediately (force refresh to bypass cache)
      await DepartmentService.forceRefresh();

      // Also update the local departments state immediately for instant UI update
      setDepartments(prevDepts => prevDepts.filter(dept => dept.id !== id));

      // Reset form if the deleted department was selected
      if (selectedDepartment && selectedDepartment.id === id) {
        resetForm();
      }

      // Emit events to notify other modules about department deletion
      window.dispatchEvent(new CustomEvent('department.deleted', {
        detail: {
          departmentId: id,
          departmentName: departmentName,
          action: 'deleted'
        }
      }));

      console.log('✅ Department deletion completed successfully');

    } catch (error) {
      console.error("Error deleting department:", error);
      alert('Error deleting department. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      department_name: "",
      associated_locations: [], // Now stores location IDs instead of names
      department_leader: "",
      members: []
    });
    setSelectedDepartment(null);
    setIsEditing(false);
    setShowEmployeesPanel(false);
  };

  // Handle location changes with JOIN TABLE endpoints - MIRROR App Users Config pattern
  const handleLocationChange = async (selectedLocationIds, skipFormRefresh = false) => {
    const oldLocationIds = formData.associated_locations || [];

    // Update UI immediately (mirror App Users Config pattern)
    setFormData({ ...formData, associated_locations: selectedLocationIds });

    // Mirror App Users Config pattern - sync with backend when editing using JOIN TABLE endpoints
    if (isEditing && selectedDepartment) {
      try {
        console.log('🔗 JOIN TABLE: Updating department locations via handleLocationChange');
        console.log('🔗 JOIN TABLE: Old location IDs:', oldLocationIds);
        console.log('🔗 JOIN TABLE: New location IDs:', selectedLocationIds);
        console.log('🔗 JOIN TABLE: Skip form refresh:', skipFormRefresh);

        // Now working with IDs directly - no need for name→ID conversion
        // Identify added and removed locations for JOIN TABLE operations (mirror App Users Config)
        const addedLocationIds = selectedLocationIds.filter(locId => !oldLocationIds.includes(locId));
        const removedLocationIds = oldLocationIds.filter(locId => !selectedLocationIds.includes(locId));

        console.log('🔗 JOIN TABLE: Added location IDs:', addedLocationIds);
        console.log('🔗 JOIN TABLE: Removed location IDs:', removedLocationIds);

        // CRITICAL: Use JOIN TABLE endpoints for each change
        // Add new links using POST /api/departments/:deptId/locations/:locId
        for (const locationId of addedLocationIds) {
          try {
            console.log('🔗 JOIN TABLE: Adding link:', selectedDepartment.id, '→', locationId);
            await CommanServices.addDepartmentToLocation(selectedDepartment.id, locationId);

            console.log('✅ JOIN TABLE: Link added successfully');
          } catch (error) {
            console.error('❌ JOIN TABLE: Error adding link:', error);
          }
        }

        // Remove old links using DELETE /api/departments/:deptId/locations/:locId
        for (const locationId of removedLocationIds) {
          try {
            console.log('🔓 JOIN TABLE: Removing link:', selectedDepartment.id, '→', locationId);
            await CommanServices.deleteDepartmentToLocation(selectedDepartment.id, locationId);

            console.log('✅ JOIN TABLE: Link removed successfully');
          } catch (error) {
            console.error('❌ JOIN TABLE: Error removing link:', error);
          }
        }

        // CRITICAL FIX: Only refresh form if not called from handleSave (to prevent reset)
        if (!skipFormRefresh) {
          console.log('🔗 JOIN TABLE: Refreshing department data from authoritative source');
          editDepartment(selectedDepartment);
        } else {
          console.log('🔗 JOIN TABLE: Skipping form refresh to prevent reset after save');
        }

        fetchDepartments(); // Update department cards with location counts

        console.log('✅ JOIN TABLE: Department location update completed successfully');
      } catch (error) {
        console.error('❌ JOIN TABLE: Error updating department locations:', error);
        console.error('❌ Error details:', error.response?.data || error.message);
        // Revert UI on error - same as App Users Config pattern
        setFormData({ ...formData, associated_locations: oldLocationIds });
        alert('Error saving location associations. Please try again.');
      }
    }
  };

  // Add department to location's associated departments (bidirectional sync)
  const addDepartmentToLocation = async (department, locationId) => {
    try {
      console.log('🔥 Starting addDepartmentToLocation process');
      console.log('🔥 Department to add:', department.department_name, department.id);
      console.log('🔥 Location ID:', locationId);

      // Fetch current location data
      const locationResponse = await CommanServices.locationszonesChipId(locationId);

      if (locationResponse.status) {
        const location = locationResponse.data;

        console.log('🔥 Current location data:', location);

        // Check current department assignments and avoid duplicates
        const currentDepartments = location.departments || [];
        const departmentExists = currentDepartments.some(dept =>
          (dept.department_id && dept.department_id === department.id) ||
          (typeof dept === 'string' && dept === department.id) ||
          dept === department.department_name
        );

        if (departmentExists) {
          console.log('🔥 Department already associated with location, skipping');
          return;
        }

        // Add department to location's departments (as DepartmentAssociation object)
        const newDepartmentAssociation = {
          department_id: department.id,
          department_name: department.department_name,
          relationship_type: 'primary'
        };

        const updatedDepartments = [...currentDepartments, newDepartmentAssociation];
        const updatedLocation = {
          ...location,
          departments: updatedDepartments
        };

        console.log('🔥 Updating location with new department');
        await CommanServices.updatelocationszones(locationId, updatedLocation);


        console.log('✅ Successfully added department to location');
      }


    } catch (error) {
      console.error('❌ Error adding department to location:', error);
      throw error;
    }
  };

  // Remove department from location's associated departments (bidirectional sync)
  const removeDepartmentFromLocation = async (department, locationId) => {
    try {
      console.log('🔥 Starting removeDepartmentFromLocation process');
      console.log('🔥 Department to remove:', department.department_name, department.id);
      console.log('🔥 Location ID:', locationId);

      // Fetch current location data
      const locationResponse = await CommanServices.locationszonesChipId(locationId);

      const location = locationResponse.data;

      console.log('🔥 Current location data:', location);

      // Remove department from location's departments (handle both ID and name)
      const currentDepartments = location.departments || [];
      const updatedDepartments = currentDepartments.filter(dept => {
        if (dept.department_id) {
          return dept.department_id !== department.id;
        }
        return dept !== department.id && dept !== department.department_name;
      });

      const updatedLocation = {
        ...location,
        departments: updatedDepartments
      };

      console.log('🔥 Updating location with removed department');
      await CommanServices.updatelocationszones(locationId, updatedLocation);


      console.log('✅ Successfully removed department from location');

    } catch (error) {
      console.error('❌ Error removing department from location:', error);
      throw error;
    }
  };

  const editDepartment = async (department) => {
    try {
      // Fetch the latest department data AND authoritative location associations
      console.log('🔗 JOIN TABLE: Fetching latest department data for editing:', department.department_name);
      const [deptResponse, locationsResponse] = await Promise.all([
        CommanServices.getbyIddepartments(department.id),
        CommanServices.getdepartmentslocation(department.id),
      ]);

      if (deptResponse.status && locationsResponse.status) {
        const latestDepartmentData = deptResponse.data;
        const authoritativeLocationData = locationsResponse.data; // Full location objects

        console.log('🔗 JOIN TABLE: Latest department data:', latestDepartmentData);
        console.log('🔗 JOIN TABLE: Authoritative associated location data:', authoritativeLocationData);

        // CRITICAL FIX: LocationSelector now expects location IDs, not names
        const authoritativeLocationIds = authoritativeLocationData.map(loc => loc.id);

        console.log('🔗 JOIN TABLE: Converted to location IDs for LocationSelector:', authoritativeLocationIds);

        // Set form data with authoritative location IDs for LocationSelector compatibility
        setFormData({
          department_name: latestDepartmentData.department_name || department.department_name,
          associated_locations: authoritativeLocationIds, // Use location IDs for LocationSelector
          department_leader: latestDepartmentData.department_leader || "",
          members: latestDepartmentData.members || []
        });

        setSelectedDepartment(latestDepartmentData);
        setIsEditing(true);
        setShowEmployeesPanel(false);

        console.log('✅ JOIN TABLE: Department edit form populated with authoritative location IDs');
      }

    } catch (error) {
      console.error('❌ JOIN TABLE: Error fetching latest department data:', error);
      // Fallback to using the passed department data
      setFormData({
        department_name: department.department_name || "",
        associated_locations: department.associated_locations || [],
        department_leader: department.department_leader || "",
        members: department.members || []
      });
      setSelectedDepartment(department);
      setIsEditing(true);
      setShowEmployeesPanel(false);
    }
  };

  // Handle showing the employees panel after successful save
  const handleShowEmployees = () => {
    if (selectedDepartment || formData.department_name.trim()) {
      setShowEmployeesPanel(true);
    }
  };


  // Get leader count from backend authoritative data (mirrors Primary Toggle pattern)
  const getDepartmentLeaderCount = (department) => {
    if (!department) return 0;

    // Prefer live members[] when present (EmployeesPanel leader toggles update members + leader_count)
    if (department.members && Array.isArray(department.members)) {
      return department.members.filter((m) => m.is_leader).length;
    }

    if (typeof department.leader_count === 'number') {
      return department.leader_count;
    }

    if (department.leaders && Array.isArray(department.leaders)) {
      return department.leaders.length;
    }

    return 0;
  };

  const addMember = () => {
    if (formData.members.length < 15) {
      setFormData({
        ...formData,
        members: [
          ...formData.members,
          {
            name: "",
            phone: "",
            email: "",
            is_leader: false,
            reporting_enabled: true,
            notification_enabled: true
          }
        ]
      });
    }
  };

  const removeMember = (index) => {
    const memberToRemove = formData.members[index];
    const newMembers = formData.members.filter((_, i) => i !== index);

    // Show hint message (non-blocking)
    if (memberToRemove.name) {
      setTimeout(() => {
        alert(`💡 Hint: ${memberToRemove.name} still lists this department in App Users Config. The department assignment was not automatically removed to avoid surprises.`);
      }, 500);
    }

    // Auto-remove leader role when member is removed
    setFormData({ ...formData, members: newMembers });
  };

  const updateMember = (index, field, value) => {
    const newMembers = [...formData.members];

    // If setting as leader, ensure only one leader per department
    if (field === 'is_leader' && value === true) {
      // Remove leader status from all other members first
      newMembers.forEach((member, idx) => {
        if (idx !== index) {
          member.is_leader = false;
        }
      });
    }

    // Update the specific member
    newMembers[index] = { ...newMembers[index], [field]: value };

    setFormData({ ...formData, members: newMembers });
  };



  const isFormValid = () => {
    return formData.department_name.trim() &&
      formData.associated_locations.length > 0;
  };

console.log("filteredDepartments", filteredDepartments);

  return (
    <div className="h-screen bg-gray-50 flex flex-1 overflow-hidden">


      {/* Main Content */}
      <div className="flex-1 flex relative" onMouseMove={handleMouseMove}>
        {/* Center Panel - Department List */}
        <div className="flex-1 p-6" style={{ marginRight: `${rightPanelWidth + 4}px` }}>
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Departments and Units</h2>
            <p className="text-sm text-gray-600 mb-4">Click on any department to view and edit details, or use the buttons below to manage departments.</p>

            {/* ENHANCED SEARCH: Powerful fuzzy search with multi-token support */}
            <div className="flex items-center space-x-4 mb-6">
              <div className="relative flex-1 max-w-2xl">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  id="departments-search"
                  placeholder="Search departments by name, leader, employee, location... (try 'leader:ahmed' or 'location:ICU')"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="pl-10 pr-10"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <Button
                onClick={resetForm}
                className="bg-green-600 hover:bg-green-700 text-white flex-shrink-0"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add New Department
              </Button>

              {searchQuery && (
                <div className="text-sm text-gray-500 flex-shrink-0">
                  {filteredDepartments.length} result{filteredDepartments.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4 max-h-[calc(100vh-250px)] overflow-y-auto pb-6">
            {filteredDepartments.map((department) => (
              <Card
                key={department.id}
                className={`cursor-pointer hover:shadow-md transition-all duration-200 border-2 ${selectedDepartment?.id === department.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
                  }`}
                onClick={() => editDepartment(department)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-800">{department.department_name}</h3>
                        <IncidentOriginChip description={department.description} />
                      </div>
                      {/* Leaders section removed per requirements */}
                      <p className="text-sm text-gray-600 mt-1">
                        {department.associated_locations?.length || 0} locations, {getDepartmentLeaderCount(department)} leaders, {department.member_count ?? department.members?.length ?? 0} members
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        {department.members.filter(m => m.notification_enabled).length > 0 && (
                          <span>🔔 {department.members.filter(m => m.notification_enabled).length} notify</span>
                        )}
                        {/* {department.members.filter(m => m.reporting_enabled).length > 0 && (
                          <span>📋 {department.members.filter(m => m.reporting_enabled).length} report</span>
                        )} */}
                      </div>
                    </div>
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(department.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Empty State for Search */}
            {searchQuery && filteredDepartments.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <Search className="w-16 h-16 mx-auto mb-4" />
                </div>
                <h3 className="text-lg font-medium text-gray-600 mb-2">No departments found</h3>
                <p className="text-gray-500 mb-4">
                  No departments match your search for "<strong>{searchQuery}</strong>"
                </p>
                <div className="text-sm text-gray-400 mb-4">
                  <p>Try searching by:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Department name (e.g., "radiology")</li>
                    <li>Leader name (e.g., "leader:ahmed")</li>
                    <li>Employee name (e.g., "employee:fatima")</li>
                    <li>Location (e.g., "location:ICU")</li>
                    <li>Phone number (e.g., "phone:5458")</li>
                  </ul>
                </div>
                <Button
                  onClick={clearSearch}
                  variant="outline"
                  className="mt-2"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear Search
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Resize Handle */}
        <div
          className="w-1 bg-gray-300 hover:bg-blue-400 cursor-col-resize transition-colors flex-shrink-0 relative group"
          onMouseDown={handleMouseDown}
          style={{ position: 'absolute', right: `${rightPanelWidth}px`, top: 0, bottom: 0, zIndex: 10 }}
        >
          <div className="absolute inset-y-0 -left-1 -right-1 group-hover:bg-blue-400 group-hover:opacity-50"></div>
        </div>

        {/* Right Panel - Department Form or Employees Panel */}
        <div
          className="bg-white shadow-lg p-6 overflow-y-auto max-h-[calc(100vh-81px)] border-l border-gray-200 absolute right-0 top-0 bottom-0"
          style={{ width: `${rightPanelWidth}px` }}
        >
          {!showEmployeesPanel ? (
            <>
              <h3 className="text-lg font-semibold mb-4">
                {isEditing ? "Edit Department" : "New Department"}
              </h3>

              {/* Department Form */}
              <div className="space-y-4">
                {/* Legacy banner removed - direct employee management workflow now */}

                <h4 className="font-medium text-gray-800 mb-4">General Department Information</h4>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Department/Unit Name <span className="text-red-500">*</span>
                  </label>
                  <Combobox
                    value={formData.department_name}
                    onValueChange={(value) => setFormData({ ...formData, department_name: value })}
                    placeholder="Type or select department name..."
                    options={(() => {
                      // Deduplicate department names (case-insensitive)
                      const uniqueDepartmentNames = new Set();
                      const deduplicatedDepartments = [];

                      departments.forEach(dept => {
                        if (dept.department_name) {
                          const normalizedName = dept.department_name.toLowerCase();
                          if (!uniqueDepartmentNames.has(normalizedName)) {
                            uniqueDepartmentNames.add(normalizedName);
                            deduplicatedDepartments.push({
                              value: dept.department_name,
                              label: dept.department_name
                            });
                          }
                        }
                      });

                      // Sort alphabetically for better UX
                      return deduplicatedDepartments.sort((a, b) =>
                        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
                      );
                    })()}
                    createLabel="Create department"
                    emptyLabel="No departments found"
                    className={`${!formData.department_name?.trim()
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300'
                      }`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Associated Location/Zones <span className="text-red-500">*</span>
                  </label>
                  <LocationSelector
                    value={formData.associated_locations}
                    onChange={handleLocationChange}
                    mode="multi"
                    allowWildcard={false}
                    placeholder="Select locations for this department"
                    className="w-full"
                  />
                </div>

                {/* Validation Message */}
                {!isFormValid() && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700 font-medium">
                      ⚠️ Please complete all required fields to save
                    </p>
                    <p className="text-xs text-red-600 mt-1">
                      Required: Department Name, Associated Locations
                    </p>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button variant="outline" onClick={resetForm} className="flex-1">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={!isFormValid()}
                    className={`flex-1 ${isFormValid()
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-gray-400 cursor-not-allowed opacity-60'
                      }`}
                  >
                    {isEditing ? "Update Department" : "Save Department"}
                  </Button>
                  {/* Show/Edit Employees Button - Only after department is saved */}
                  {selectedDepartment && (
                    <Button
                      onClick={handleShowEmployees}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Show/Edit Employees
                    </Button>
                  )}
                </div>
              </div>
            </>
          ) : (
            /* Employees Panel */
            <EmployeesPanel
              department={selectedDepartment}
              onClose={() => setShowEmployeesPanel(false)}
              setSelectedDepartment={setSelectedDepartment}
              setDepartments={setDepartments}
            />
          )}
        </div>
      </div>
    </div>
  );
};
export default DepartmentsAndUnits;